import React from "react";

/**
 * Gravity Lab — launch page.
 *
 * A hub that sends students to the three Lesson 18 simulations, in the order
 * the Station Worksheet follows: Newton's Cannon → Gravity Ranking Challenge →
 * Sun & Earth Orbit Lab.
 *
 * Links point to the three sims on app.kingsacademy.edu.jo. To change them,
 * edit the `href` (and `external`) values on each entry in STATIONS below.
 *
 * Self-contained. Only dependency is React. Default export.
 * Next.js App Router: add  'use client'  as the first line of the file.
 */

const C = {
  navy: "#1B2A4A", navyDeep: "#14203A", gold: "#C9A227", goldSoft: "#E0C56A",
  cream: "#F7F3E8", card: "#FBF8F0", blue: "#2E6E8E", terra: "#C0603A",
  ink: "#2B2B2B", mute: "#6B7280", line: "#E3DCC6",
};

const STATIONS = [
  {
    n: 1,
    name: "Newton’s Cannon",
    href: "https://app.kingsacademy.edu.jo/cannon",
    external: true,
    accent: C.terra,
    task: "Fire the cannon off the mountaintop and find the speed ranges where the ball crashes, orbits, or escapes.",
    worksheet: "Worksheet Station 1 · Questions 1–4",
    glyph: "cannon",
  },
  {
    n: 2,
    name: "Gravity Ranking Challenge",
    href: "https://app.kingsacademy.edu.jo/gravity",
    external: true,
    accent: C.gold,
    task: "Rank each pairing from strongest to weakest pull. Lock in your answer, then defend it with mass and distance.",
    worksheet: "Worksheet Station 2 · Questions 5–6",
    glyph: "ranking",
  },
  {
    n: 3,
    name: "Sun & Earth Orbit Lab",
    href: "https://app.kingsacademy.edu.jo/orbitlab",
    external: true,
    accent: C.blue,
    task: "Change the masses and the distance and watch the orbit reshape — circle, ellipse, wobble, or escape.",
    worksheet: "Worksheet Station 3 · Questions 7–10",
    glyph: "orbit",
  },
];

function Glyph({ kind, color }) {
  // small inline SVG marks — one per station, drawn to the station's accent
  const common = { width: 46, height: 46, viewBox: "0 0 46 46", fill: "none", "aria-hidden": true };
  if (kind === "cannon") {
    return (
      <svg {...common}>
        <circle cx="23" cy="23" r="21" fill={color} opacity="0.14" />
        <path d="M12 30 L26 16" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <circle cx="11" cy="31" r="5" fill={color} />
        <path d="M30 14 q4 -3 6 1" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      </svg>
    );
  }
  if (kind === "ranking") {
    return (
      <svg {...common}>
        <circle cx="23" cy="23" r="21" fill={color} opacity="0.14" />
        <rect x="13" y="12" width="20" height="5" rx="2.5" fill={color} />
        <rect x="13" y="20.5" width="15" height="5" rx="2.5" fill={color} opacity="0.75" />
        <rect x="13" y="29" width="10" height="5" rx="2.5" fill={color} opacity="0.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="23" cy="23" r="21" fill={color} opacity="0.14" />
      <ellipse cx="23" cy="23" rx="16" ry="9" stroke={color} strokeWidth="2.5" fill="none" />
      <circle cx="23" cy="23" r="5" fill={color} />
      <circle cx="39" cy="23" r="3" fill={color} opacity="0.8" />
    </svg>
  );
}

export default function LaunchPage() {
  return (
    <div style={S.root}>
      <style>{`
        .lp-card{text-decoration:none;transition:transform .08s ease, box-shadow .15s ease;}
        .lp-card:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(27,42,74,.14)}
        .lp-card:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(201,162,39,.5)}
      `}</style>

      <header style={S.band}>
        <div style={S.eyebrow}>Lesson 18 · Universal Gravitation</div>
        <div style={S.title}>Gravity Lab</div>
        <div style={S.sub}>Three stations — work through them in order and fill in your worksheet as you go</div>
      </header>

      <div style={S.grid}>
        {STATIONS.map((st) => (
          <a
            key={st.n}
            className="lp-card"
            style={{ ...S.cardLink, borderTopColor: st.accent }}
            href={st.href}
            {...(st.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            <div style={S.cardHead}>
              <div style={{ ...S.stationNum, background: st.accent }}>{st.n}</div>
              <Glyph kind={st.glyph} color={st.accent} />
            </div>
            <div style={S.cardName}>{st.name}</div>
            <div style={S.cardTask}>{st.task}</div>
            <div style={S.cardFoot}>
              <span style={{ ...S.wsTag, color: st.accent }}>{st.worksheet}</span>
              <span style={{ ...S.launch, background: st.accent }}>Launch →</span>
            </div>
          </a>
        ))}
      </div>

      <div style={S.ruleRow}>
        <div style={{ ...S.ruleBox, background: C.blue }}>
          <b>Gravity LOVES mass.</b> More mass → stronger pull.
        </div>
        <div style={{ ...S.ruleBox, background: C.terra }}>
          <b>Gravity HATES distance.</b> Farther apart → weaker pull (distance²).
        </div>
      </div>

      <div style={S.setupNote}>
        Links point to <code style={S.code}>app.kingsacademy.edu.jo</code>. To change them later, edit the
        <code style={S.code}> href</code> values in <code style={S.code}>LaunchPage.jsx</code>.
      </div>
    </div>
  );
}

const S = {
  root: { maxWidth: 1040, margin: "0 auto", padding: 16, boxSizing: "border-box", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", color: C.ink },
  band: { background: C.navy, borderRadius: 14, padding: "20px 24px", marginBottom: 18, backgroundImage: `linear-gradient(135deg, ${C.navy}, ${C.navyDeep})` },
  eyebrow: { fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: C.gold, fontWeight: 700, marginBottom: 6 },
  title: { fontFamily: "Georgia,serif", fontSize: "clamp(26px,5vw,40px)", fontWeight: 700, color: C.cream, lineHeight: 1.05 },
  sub: { fontSize: 15, fontStyle: "italic", color: C.goldSoft, marginTop: 6 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 18 },
  cardLink: { display: "flex", flexDirection: "column", gap: 10, background: C.card, border: `1px solid ${C.line}`, borderTop: "4px solid", borderRadius: 12, padding: 18, color: C.ink, minWidth: 0 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  stationNum: { width: 34, height: 34, borderRadius: 9, color: C.cream, fontFamily: "Georgia,serif", fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" },
  cardName: { fontFamily: "Georgia,serif", fontSize: 20, fontWeight: 700, color: C.navy, lineHeight: 1.2, textWrap: "balance" },
  cardTask: { fontSize: 13.5, lineHeight: 1.5, color: C.ink, flex: 1 },
  cardFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 4, flexWrap: "wrap" },
  wsTag: { fontSize: 11.5, fontWeight: 700 },
  launch: { fontSize: 13, fontWeight: 700, color: "#fff", padding: "6px 12px", borderRadius: 7 },
  ruleRow: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  ruleBox: { flex: "1 1 240px", minWidth: 0, color: C.cream, fontSize: 13.5, lineHeight: 1.45, padding: "11px 15px", borderRadius: 10 },
  setupNote: { fontSize: 12.5, lineHeight: 1.6, color: C.mute, background: "#fff", border: `1px dashed ${C.line}`, borderRadius: 10, padding: "12px 14px" },
  code: { fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 12, background: "#f0ece0", color: C.navy, padding: "1px 5px", borderRadius: 4 },
};